import assertNever from "assert-never";
import { Page } from "../../../activities/AppPage";
import { isLoading, Loading } from "../../../Loading";
import { InstaPayVisit } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { translate } from "../../../translate";
import { useModalV2 } from "../../AppModals";
import { getFormattedArrivalTime } from "../utils/get-instapay-arrival-time";

export default function useInstaPayVisitItemClickHandler(params: {
  caregiver: AppCaregiver | Loading;
  currentWeek: number;
  onRequestNavigate: (page: Page) => void;
}) {
  const { createModal } = useModalV2();

  return (visit: InstaPayVisit) => {
    switch (visit.status.type) {
      case "EligibleInstaPay":
        if (isLoading(params.caregiver) || !params.caregiver.isInstapayAvailable) {
          return createModal({
            type: "info",
            title: translate(STRINGS.COMING_SOON),
          });
        }

        return params.onRequestNavigate({
          type: "SelectInstaPayVisits",
          weekNum: params.currentWeek,
        });
      case "Paid":
        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_PAID_MODAL_TITLE).replace(
            "{x}",
            `$${(visit.status.amount / 100).toFixed(2)}`
          ),
          message: translate(STRINGS.PAYROLL_STATUS_PAID_MODAL_MESSAGE),
        });
      case "InstaPayInProgress":
        return createModal({
          type: "success",
          title: translate(STRINGS.PAYROLL_STATUS_IN_PROGRESS_TITLE),
          message: translate(STRINGS.PAYROLL_STATUS_IN_PROGRESS_MESSAGE).replace(
            "{PROCESS_TIME}",
            getFormattedArrivalTime(visit.status.requestedAt)
          ),
        });
      case "MissingClockTimes":
        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_MISSING_CLOCK_TIMES_TITLE),
          message: translate(STRINGS.PAYROLL_STATUS_MISSING_CLOCK_TIMES_MESSAGE),
        });
      case "VisitNotCompleted":
        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_VISIT_NOT_COMPLETED_MODAL_TITLE),
          message: translate(STRINGS.PAYROLL_STATUS_VISIT_NOT_COMPLETED_MODAL_MESSAGE),
        });
      case "RegularPay":
        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_REGULAR_PAY_MODAL_TITLE),
          message: translate(STRINGS.PAYROLL_STATUS_REGULAR_PAY_MODAL_MESSAGE),
        });
      case "PendingIssues": {
        const messages: string[] = [];

        if (visit.status.clockTimesNotMobile) {
          messages.push(translate(STRINGS.CLOCK_TIMES_NOT_MOBILE));
        }
        if (visit.status.earlyOrLateClockTimes) {
          messages.push(translate(STRINGS.EARLY_OR_LATE_CLOCKS));
        }
        if (visit.status.visitTooShort) {
          messages.push(translate(STRINGS.VISIT_TOO_SHORT));
        }
        if (visit.status.missingPersonalCareDuty) {
          messages.push(translate(STRINGS.MISSING_PERSONAL_CARE_DUTY));
        }
        if (visit.status.insufficientDuties) {
          messages.push(translate(STRINGS.INSUFFICIENT_DUTIES));
        }
        if (visit.status.outOfRange) {
          messages.push(translate(STRINGS.OUT_OF_RANGE));
        }
        if (visit.status.agencyIssues) {
          messages.push(translate(STRINGS.AGENCY_ISSUES));
        }

        const message =
          messages.length !== 1
            ? `<ul style="text-align: left;">${messages
                .map((message) => `<li>${message}</li>`)
                .join("")}</ul>`
            : messages[0];

        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_PENDING_ISSUE_MODAL_TITLE),
          message,
          options: {
            messageAsHtml: true,
          },
        });
      }
      case "TechnicalIssue":
        return createModal({
          type: "info",
          title: translate(STRINGS.PAYROLL_STATUS_TECHNICAL_ISSUE_MODAL_TITLE),
          message: translate(STRINGS.PAYROLL_STATUS_TECHNICAL_ISSUE_MODAL_MESSAGE),
        });
      default:
        assertNever(visit.status);
    }
  };
}
